import { Box, Clock, Monitor } from 'lucide-react';

import type { AppSwitcherApp } from './AppSwitcher';

/**
 * Liste **canonique** des apps de la suite Bill, partagee entre les sidebars
 * de chaque produit (BStock, BStaff, BDisplay, futurs).
 *
 * Pour ajouter un nouveau produit : ajouter une entry ici, le bouton apparait
 * automatiquement dans l'AppSwitcher des 3 sidebars existantes.
 *
 * Les URLs sont lues depuis les variables d'env Vite `VITE_<APP>_URL` injectees
 * au build de chaque frontend. Les fallbacks `localhost:<port>` correspondent
 * aux ports dev par defaut (BStock 3000, BStaff 3100, BDisplay 3200).
 *
 * Les icones sont des composants `lucide-react` (deja dep de @bill/ui).
 */
export const BILL_APPS: AppSwitcherApp[] = [
  {
    id: 'bstock',
    name: 'BStock',
    description: 'Stock & rentabilite',
    icon: <Box className="h-5 w-5" />,
    url: import.meta.env.VITE_BSTOCK_URL || 'http://localhost:44000',
  },
  {
    id: 'bstaff',
    name: 'BStaff',
    description: 'Pointeuse & RH',
    icon: <Clock className="h-5 w-5" />,
    url: import.meta.env.VITE_BSTAFF_URL || 'http://localhost:44100',
  },
  {
    id: 'bdisplay',
    name: 'BDisplay',
    description: 'Affichage dynamique',
    icon: <Monitor className="h-5 w-5" />,
    url: import.meta.env.VITE_BDISPLAY_URL || 'http://localhost:44210',
  },
];

/**
 * Filtre les apps a afficher dans l'AppSwitcher selon les produits accessibles
 * a l'user (Modular Access Control US-MAC.6).
 *
 * @param apps Liste canonique (typiquement `BILL_APPS`)
 * @param availableProducts Liste retournee par `GET /api/auth/me` dans
 *   `data.moduleAccess.availableProducts` (format `['BSTOCK', 'BSTAFF']`
 *   en uppercase). Si `null` ou `undefined` → on retourne toutes les apps
 *   (mode legacy / fallback : moduleAccess pas encore present cote backend
 *   ou erreur de chargement).
 *
 * Le matching est case-insensitive : `app.id` est en lowercase (`'bstock'`),
 * `availableProducts` en uppercase (`'BSTOCK'`).
 */
export function filterAppsByProducts(
  apps: AppSwitcherApp[],
  availableProducts: readonly string[] | null | undefined,
): AppSwitcherApp[] {
  if (!availableProducts) return apps;
  const allowed = new Set(availableProducts.map((p) => p.toLowerCase()));
  return apps.filter((app) => allowed.has(app.id.toLowerCase()));
}
